import React from "react";
import {Check} from "@sparkware/app-utils";
import {observer} from 'mobx-react';
import {OrbitComponent} from '../../../base-components/orbit-component/orbit-component';
import {LiveCasinoGridBox} from "./boxes/grid/live-casino-grid.box";
import {observable, action, makeObservable} from "mobx";
import {LiveCasinoGridItemBox} from "./boxes/grid-item/live-casino-grid-item.box";
import {LiveCasinoArenaGridItem} from './live-casino-arena-grid-item.component';
import {LiveCasinoGridItemSkeletonBox} from "./boxes/grid/skeleton/live-casino-grid-item-skeleton.box";
import {LiveCasinoGridItemSkeletonTitleBox} from "./boxes/grid/skeleton/live-casino-grid-item-skeleton-title.box";
import {LiveCasinoGridItemSkeletonWrapperBox} from "./boxes/grid/skeleton/live-casino-grid-item-skeleton-wrapper.box";
import {LiveCasinoPromotionalBannerConfiguration} from '../../../../services/configurations/config-data-sections/arenas.configuration';
import {BannerComponent} from "../../../banner/banner.component";
import {
    LiveTableTabConfiguration
} from "../../../../services/live-table-feeds-service/live-table-feeds.service.interface";
import {SingleGameComponent} from "../../../single-game/single-game.component";

export interface ILiveCasinoArenaGridProps {
    seoCategory: string;
    useAISortedList: boolean;
    promotionalBannerConfig?: LiveCasinoPromotionalBannerConfiguration;
}

@observer
export class LiveCasinoArenaGrid extends OrbitComponent<ILiveCasinoArenaGridProps> {
    private _gameElementRef: HTMLDivElement | null = null;
    private _gridElementRef: HTMLDivElement | null = null;

    constructor(props: ILiveCasinoArenaGridProps) {
        super(props);

        makeObservable(this);
    }

    get liveTabConfigs(): LiveTableTabConfiguration | null {
        return this.services.liveTableFeeds.currentArenaTabConfiguration;
    }

    @observable.ref
    private _cmsBannerPosition: number = 0;

    @observable.ref
    private _numberOfGamesPerRow: number = 0;

    private _numberOfGames: number = 0;

    private _captureGameElementRef = (gameElement: HTMLDivElement | null) => {
        this._gameElementRef = gameElement;
        this._computeNumberOfGamesInARow();
    };

    private _captureGridElementRef = (gridElement: HTMLDivElement | null) => {
        this._gridElementRef = gridElement;
        this._computeNumberOfGamesInARow();
    };

    @action.bound
    private _setCMSBannerPosition() {
        let cmsBannerPositionFromConfig = this._numberOfGamesPerRow * this.props?.promotionalBannerConfig?.displayRowPlace;
        this._cmsBannerPosition = this._numberOfGames > cmsBannerPositionFromConfig ? cmsBannerPositionFromConfig : this._numberOfGames;
    }

    private _computeNumberOfGamesInARow() {

        if (!this._gridElementRef || !this._gameElementRef) return;

        try {
            this._numberOfGamesPerRow = Math.round(Number(this._gridElementRef["offsetWidth"]) / Number(this._gameElementRef["offsetWidth"]));
            this._setCMSBannerPosition();
        } catch (error) {
            this.services.logger.error("Failed to compute number of live games in a row", error);
        }
    }


    private _renderPromotionalBanner() {

        if (Check.isNullOrUndefined(this.props.promotionalBannerConfig)) return null;

        let promotionalBanner = this.liveTabConfigs?.headerConfiguration?.banners?.promotionalBanner;
        if (!promotionalBanner) {
            promotionalBanner = this.props.promotionalBannerConfig;
        }


        return (
            <BannerComponent
                templateID={promotionalBanner.templateID}
                componentID={promotionalBanner.componentID}
                contentID={promotionalBanner.contentID}
                bannerAdminPlaceholderId={promotionalBanner.bannerAdminPlaceholderId}
                showPlaceholder={false}
            />
        );
    }

    private _renderGridSkeleton() {

        let skeletons = [];

        for (var i = 0; i < 12; i++) {
            skeletons.push(
                <LiveCasinoGridItemBox key={i}>
                    <LiveCasinoGridItemSkeletonWrapperBox>
                        <LiveCasinoGridItemSkeletonBox />
                        <LiveCasinoGridItemSkeletonTitleBox>Loading</LiveCasinoGridItemSkeletonTitleBox>
                    </LiveCasinoGridItemSkeletonWrapperBox>
                </LiveCasinoGridItemBox>
            );
        }

        return skeletons;
    }

    private _renderAbGameInfo(tableId: number, gameIndex: number, key: number, listTitle: string) {
        return this.services.abTestingFeatureDataProvider.staticLiveArena.isEnabled ?
            <SingleGameComponent key={this.services.games.gamesMap[tableId].gameType}
                gameType={this.services.games.gamesMap[tableId].gameType}
                gameIndex={gameIndex}
                seoCategory={this.props.seoCategory}
                listTitle={listTitle}
                useSquareIcons={true}
                disableGameInfoHover={false} /> :
            <LiveCasinoArenaGridItem
                tableId={tableId}
                gameInfo={this.services.games.gamesMap[tableId]}
                gameIndex={gameIndex}
                listTitle={listTitle}
                liveTable={this.services.liveTableFeeds.getTableInfo(tableId)}
                seoCategory={this.props.seoCategory}
                captureGameElementRef={this._captureGameElementRef}
            />
    }

    private _renderGridItem(tableId: number, gameIndex: number, key: number) {

        let listTitle = this.services.liveTableFeeds.currentArenaTabConfiguration?.gridConfiguration.title;

        //this if is a duplicate with the one in live-casino-arena-navbar-button.component.tsx
        // in the future we should look for a way to refactor this for_you logic and have it come from the admin somehow, have only one source of truth
        if (this.services.user.isAuthorized && listTitle === "TOP_EVO_GAMES") {
            listTitle = "FOR_YOU_GAMES";
        }
        const shouldRenderPromoBanner = gameIndex === (this._cmsBannerPosition - 1);

        return (
            <React.Fragment key={key}>
                {this._renderAbGameInfo(tableId, gameIndex, key, listTitle)}                
                {
                    shouldRenderPromoBanner && this._renderPromotionalBanner()
                }
            </React.Fragment>
        );
    }

    private _renderGames() {

        let gameIds: number[] = [];


        if (this.liveTabConfigs) {
            gameIds = this.services.liveTableFeeds.getLiveGames();
        }

        if (gameIds.length === 0) {
            return this._renderGridSkeleton();
        } else {
            this._numberOfGames = gameIds.length;
            return gameIds.map((tableId, gameIndex) => this._renderGridItem(tableId, gameIndex, tableId));
        }
    }

    componentDidMount() {
        super.componentDidMount();

        this.reaction(() => [
            this.services.device.os.isPC,
            this.services.device.isTablet,
            this.services.device.isMobile
        ], () => this._setCMSBannerPosition(), {fireImmediately: true});
    }

    render() {

        return (
            <LiveCasinoGridBox ref={this._captureGridElementRef} className="cy-live-casino-grid">
                {this._renderGames()}
            </LiveCasinoGridBox>
        );
    }
}

